import { useI18n as useVueI18n } from 'vue-i18n'
import { computed } from 'vue'
import { useLanguageStore } from '@/stores/language'

export function useI18n() {
  const { t, locale, availableLocales } = useVueI18n()
  const languageStore = useLanguageStore()

  // Função para trocar idioma - usando o language store como fonte da verdade
  const changeLanguage = (newLocale) => {
    languageStore.setSystemLanguage(newLocale)
    // O setSystemLanguage já sincroniza com o i18n automaticamente
  }

  // Computed para verificar se é português - usando language store como fonte da verdade
  const isPortuguese = computed(() => languageStore.currentSystemLanguage === 'pt-BR')

  // Computed para verificar se é inglês - usando language store como fonte da verdade
  const isEnglish = computed(() => languageStore.currentSystemLanguage === 'en-US')

  // Computed para verificar se é espanhol - usando language store como fonte da verdade
  const isSpanish = computed(() => languageStore.currentSystemLanguage === 'es-ES')

  // Computed para o locale atual - usando language store como fonte da verdade
  const currentLocale = computed(() => languageStore.currentSystemLanguage)

  // Função para formatar números - usando language store como fonte da verdade
  const formatNumber = (number, options = {}) => {
    if (!number || isNaN(number)) return '0,00'
    return new Intl.NumberFormat(languageStore.currentSystemLanguage, options).format(number)
  }

  // Função para formatar moeda - usando language store como fonte da verdade
  const formatCurrency = (amount, currency = null) => {
    const currencyMap = {
      'pt-BR': 'BRL',
      'en-US': 'USD',
      'es-ES': 'EUR',
    }

    return new Intl.NumberFormat(languageStore.currentSystemLanguage, {
      style: 'currency',
      currency: currency || currencyMap[languageStore.currentSystemLanguage],
    }).format(amount)
  }

  // Função para formatar data - usando language store como fonte da verdade
  const formatDate = (date, options = {}) => {
    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }

    return new Intl.DateTimeFormat(languageStore.currentSystemLanguage, {
      ...defaultOptions,
      ...options,
    }).format(new Date(date))
  }

  // Função para formatar data relativa
  const formatRelativeDate = (date) => {
    const now = new Date()
    const targetDate = new Date(date)
    const diffTime = now - targetDate
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return t('dateTime.today')
    if (diffDays === 1) return t('dateTime.yesterday')
    if (diffDays === -1) return t('dateTime.tomorrow')

    return formatDate(date)
  }

  // Função para pluralização
  const pluralize = (key, count, options = {}) => {
    return t(key, { count, ...options })
  }

  return {
    t,
    locale,
    currentLocale,
    availableLocales,
    changeLanguage,
    isPortuguese,
    isEnglish,
    isSpanish,
    formatNumber,
    formatCurrency,
    formatDate,
    formatRelativeDate,
    pluralize,
  }
}
